import {Button, css, Stack, styled, SwipeableDrawer, Typography} from '@mui/material';
import Icon from 'presentation/icons';
import {
	StyledButtonProps,
	StyledSwipeableDrawerProps,
	type DrawerContentProps,
	type PullerContainerProps,
	type StyledHeaderDrawerProps,
	type StyledTitleDrawerProps,
} from './types';

const StyledSwipeableDrawer = styled(SwipeableDrawer)<StyledSwipeableDrawerProps>`
	& .MuiDrawer-paper {
		border-radius: 8px 8px 0 0;
		max-height: ${({$customMaxHeight}) => $customMaxHeight ?? '65%'};
		overflow: visible;

		height: ${({$customHeight}) => $customHeight ?? 'auto'};

		${({theme, $customWidth, $customMaxWidth}) => css`
			${theme.breakpoints.up('sm')} {
				width: ${$customWidth ?? '50%'};
				max-width: ${$customMaxWidth ?? '70%'};
				height: 100%;
				max-height: 100%;
				border-radius: 8px 0 0 8px;
				padding-top: 16px;
			}
		`}
	}
`;

const PullerContainer = styled(Stack)<PullerContainerProps>`
	width: 100%;
	padding: ${({$pullerPadding}) => $pullerPadding ?? '16px 0'};
	justify-content: center;
	align-items: center;
`;

const Puller = styled(Stack)`
	width: 30px;
	height: 6px;
	border-radius: 12px;
	${({theme}) => css`
		background-color: ${theme.colors.gray['30Percent']};
	`}
`;

const HeaderDrawer = styled(Stack)<StyledHeaderDrawerProps>`
	${({theme, $padding}) => css`
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		padding: ${$padding ?? '0px 16px 16px'};

		${theme.breakpoints.up('sm')} {
			padding: ${$padding ?? '0px 24px 8px'};
		}
	`}
`;

const ContentDrawer = styled(Stack)<DrawerContentProps>`
	flex: 1;
	padding: ${({$customPadding}) => $customPadding ?? '0px 16px 16px'};
	overflow: auto;

	${({theme}) => theme.breakpoints.up('sm')} {
		padding: ${({$customPadding}) => $customPadding ?? '0px 24px 16px'};
	}
`;

const FooterDrawer = styled(Stack)`
	justify-content: center;
	align-items: center;
	gap: 8px;
`;

const TitleDrawer = styled(Typography)<StyledTitleDrawerProps>`
	${({$fontSize, $fontWeight, $lineHeight}) => css`
		font-size: ${$fontSize ?? '1.25rem'};
		font-weight: ${$fontWeight ?? 600};
		line-height: ${$lineHeight ?? 'auto'};
	`}
`;

const StyledButton = styled(Button)<StyledButtonProps>`
	padding: 4px 12px 4px 12px;
	${({theme, $isSelected}) => css`
		background-color: ${$isSelected
			? theme.palette.other.inversedColorIconBg
			: theme.palette.background.paper};
		color: ${$isSelected ? theme.palette.primary.main : theme.palette.text.secondary};
		border-color: ${$isSelected ? theme.palette.primary.main : theme.colors.gray['40Percent']};

		&:hover {
			background-color: theme.palette.action.hover;
			border-color: ${$isSelected ? theme.palette.primary.dark : theme.palette.divider};
		}
	`}
`;

const StyledStackSwitch = styled(Stack)`
	flex-direction: row;
	gap: 8px;
	align-items: center;
	padding-bottom: 8px;
`;

const StyledTypographySwitch = styled(Typography)`
	font-size: 1rem;
	font-weight: 400;
	line-height: 24px;
	letter-spacing: 0.15px;
	flex: 1;

	${({theme}) => theme.breakpoints.up('lg')} {
		margin-right: initial;
	}
`;

const StyledIcon = styled(Icon)`
	color: ${({theme}) => theme.colors.light.text.secondary};
`;

export {
	StyledSwipeableDrawer,
	StyledIcon,
	StyledTypographySwitch,
	StyledStackSwitch,
	StyledButton,
	TitleDrawer,
	FooterDrawer,
	ContentDrawer,
	HeaderDrawer,
	Puller,
	PullerContainer,
};
